/*
 * The identity client of an Application (audit Phase 3.3). Replaces
 * wwwroot/js/Identity/Client.js on the Phase 3.1 core. No jQuery.
 *
 * One surface: the Identity tab of Application Detail. It lists the clients the
 * application owns (an interactive sign-in client, service clients) and opens each
 * into its OpenIddict registration -- client id, secrets, grant types, scopes,
 * redirect URIs -- with, beneath it, the machine roles that hydrate onto that
 * client's client_credentials tokens. DetailByResourceId renders the list.
 *
 * Every write re-renders from the server rather than leaving the edited form on
 * screen. The secret is one-way hashed on save, so a typed value must not stay
 * visible implying it is still readable; the machine-roles warning is derived
 * from the grant types that were just saved; and the re-render is what takes
 * the form out of create mode -- without it the hidden Id stays 0 and a second
 * Save creates a duplicate.
 *
 * `mode: 'replaceWith'` throughout: the Detail partial's own root element IS
 * <div id="IdentityClientContainer">, so a plain innerHTML swap nests a second
 * container with the same id inside the first on every re-render.
 */
import { ajaxJson, ajaxSwap, failToast, summaryOf } from '../core/ajax';
import { one } from '../core/dom';
import { serialize } from '../core/forms';
import { confirm } from '../core/modal';
import { toast } from '../core/toast';
import { setValidationModelState } from '../core/validation';

const CONTAINER = 'IdentityClientContainer';
const FORM = 'IdentityClientForm';
const TAB = 'ApplicationIdentityTab';
const SAVE_BUTTON = '#IdentityClientSaveButton';

function value(id: string): string {
    return (one(`#${id}`) as HTMLInputElement | null)?.value ?? '';
}

/**
 * The save button's own spinner. Scoped to the button, not to the container:
 * a container-wide '.fa-spinner' also matches the machine-roles spinners
 * below, which would flash on every save and tie their state to this request.
 */
function saving(working: boolean): void {
    const button = one(SAVE_BUTTON);
    if (!button) return;
    for (const node of Array.from(button.querySelectorAll('.fa-save'))) node.classList.toggle('d-none', working);
    for (const node of Array.from(button.querySelectorAll('.fa-spinner'))) node.classList.toggle('d-none', !working);
}

/** The model-level ModelState entry, onto the form's own validation summary. */
function showModelState(data: unknown): boolean {
    const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
    if (modelState) setValidationModelState(modelState, `#${FORM}`);
    return true;
}

export const Client = {
    DetailByResourceId(resourceId: number | string): Promise<void> {
        return ajaxSwap({
            container: CONTAINER,
            url: `IdentityClient/Resource/${resourceId}`,
            mode: 'replaceWith',
            spinnerInto: true,
            alertInto: TAB,
            alertTitle: 'Failed to render Identity Client detail',
        });
    },

    /** Back to the application's client list. */
    List(): void {
        void Client.DetailByResourceId(value('ResourceId') || value('ApplicationId'));
    },

    /** One client of the list, into the Detail form. */
    Open(clientId: number | string): void {
        void ajaxSwap({
            container: CONTAINER,
            url: `IdentityClient/${clientId}`,
            mode: 'replaceWith',
            spinnerInto: true,
            alertInto: TAB,
            alertTitle: 'Failed to render Identity Client detail',
        });
    },

    Create(): void {
        void ajaxSwap({
            container: CONTAINER,
            url: `IdentityClient/Resource/${value('ApplicationId')}/Create`,
            mode: 'replaceWith',
            alertInto: 'ApplicationDetailContainer',
            alertTitle: 'Failed to render Identity Client detail',
        });
    },

    CreateSave(): void {
        void save('IdentityClient', 'POST');
    },

    Update(): void {
        void save(`IdentityClient/${value('Id')}`, 'PUT');
    },

    Delete(): void {
        const clientId = value('Id');
        const resourceId = value('ResourceId');
        void confirm({
            title: 'Remove identity client',
            html: 'Remove this identity client configuration?<br/><br/>'
                + '<span class="text-danger">Nobody can sign in to this application afterwards, and any '
                + 'integration using its client credentials stops working as soon as its current token '
                + 'expires.</span>',
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                saving(true);
                void ajaxJson({
                    url: `IdentityClient/${clientId}`,
                    method: 'DELETE',
                    alertInto: TAB,
                    errorTitle: 'Failed to remove the Identity Client configuration',
                    onSuccess: () => {
                        toast('success', 'Identity client removed');
                        void Client.DetailByResourceId(resourceId);
                    },
                    onFailure: showModelState,
                    onComplete: () => saving(false),
                });
            },
        });
    },

    // ---------------------------------------------------------- machine roles
    //
    // Granted roles hydrate onto this client's client_credentials tokens at
    // issuance, so every write here changes standing machine authority. Each
    // one reloads the whole section rather than patching rows: the server
    // decides what is grantable (already-granted and Administrator are filtered
    // out), and a hand-patched row would drift from that.
    //
    // The section's FIRST render is server-side, inside IdentityClient/Detail:
    // the Identity tab arrives through the generic data-tab-url handler, which
    // swaps HTML and runs no per-view script. LoadRoles is the refresh only.

    LoadRoles(): void {
        const clientId = value('Id');
        if (!clientId || Number(clientId) <= 0) return;
        void ajaxSwap({
            container: 'IdentityClientRolesSection',
            url: `IdentityClient/${clientId}/Roles`,
            alertInto: TAB,
            alertTitle: 'Failed to render machine roles',
        });
    },

    RoleAddView(): void {
        const clientId = value('IdentityClientRolesClientId');
        const button = one('#IdentityClientRoleAddButton');
        button?.classList.add('d-none');
        void ajaxSwap({
            container: 'IdentityClientRoleAddContainer',
            url: `IdentityClient/${clientId}/RoleAdd`,
            busy: '#IdentityClientRoleBusy',
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to display the role picker', error);
                button?.classList.remove('d-none');
                return true;
            },
        });
    },

    /**
     * The application select changed -- re-fetch the picker in the new scope.
     * Value 0 is the "(Global roles)" entry; the server maps it to null, since
     * globals are stored with no ApplicationId.
     */
    RoleScopeChange(): void {
        const clientId = value('IdentityClientRolesClientId');
        const applicationId = value('IdentityClientRoleScope');
        void ajaxSwap({
            container: 'IdentityClientRoleAddContainer',
            url: `IdentityClient/${clientId}/RoleAdd?applicationId=${encodeURIComponent(applicationId)}`,
            busy: '#IdentityClientRoleBusy',
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to list roles for that application', error);
                return true;
            },
        });
    },

    RoleAddCancel(): void {
        one('#IdentityClientRoleAddContainer')?.replaceChildren();
        one('#IdentityClientRoleAddButton')?.classList.remove('d-none');
    },

    RoleAddSave(): void {
        const clientId = value('IdentityClientRolesClientId');
        const roleId = value('IdentityClientRoleAddId');
        if (!roleId) return;
        // A real <button>: the legacy code set .prop('disabled') on a
        // <span role="button">, which has no such property, so a second click
        // during the request granted the role twice.
        const button = one('#IdentityClientRoleSaveButton') as HTMLButtonElement | null;
        if (button) button.disabled = true;
        void ajaxJson({
            url: `IdentityClient/${clientId}/RoleAdd/${roleId}`,
            method: 'POST',
            busy: '#IdentityClientRoleBusy',
            onSuccess: () => {
                toast('success', 'Granted role to the machine principal',
                    'It takes effect on the client\'s next token request.');
                Client.LoadRoles();
            },
            onFailure: failToast('Failed to grant the role'),
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to grant the role', error);
                return true;
            },
            onComplete: () => { if (button) button.disabled = false; },
        });
    },

    RoleRemove(roleId: number | string): void {
        const clientId = value('IdentityClientRolesClientId');
        const control = one(`#IdentityClientRoleRemove${roleId}`);
        void confirm({
            title: 'Revoke machine role',
            html: 'Revoke this role from the machine principal?<br/><br/>'
                + 'Tokens already issued keep their claims until they expire, so the integration may '
                + 'keep working for the remainder of its token lifetime.',
            confirmText: 'Revoke',
            variant: 'danger',
            onConfirm: () => {
                control?.classList.add('d-none');
                void ajaxJson({
                    url: `IdentityClient/${clientId}/RoleRemove/${roleId}`,
                    method: 'POST',
                    busy: `#IdentityClientRoleBusy${roleId}`,
                    onSuccess: () => {
                        toast('success', 'Revoked the role');
                        Client.LoadRoles();
                    },
                    onFailure: data => {
                        toast('danger', 'Failed to revoke the role', summaryOf(data));
                        control?.classList.remove('d-none');
                        return true;
                    },
                    onError: (_xhr, _status, error) => {
                        toast('danger', 'Failed to revoke the role', error);
                        control?.classList.remove('d-none');
                        return true;
                    },
                });
            },
        });
    },

    // ---------------------------------------------------------------- secrets
    //
    // Secrets are hashed one way, so they can only be REPLACED, never read.
    // The flag tells the server that the (possibly empty) CSV field is meant
    // as the new value -- without it a blank field would erase every secret on
    // any unrelated save.

    EditSecrets(): void {
        toggleSecrets(true);
    },

    EditSecretsCancel(): void {
        toggleSecrets(false);
    },
};

function toggleSecrets(editing: boolean): void {
    for (const node of Array.from(document.querySelectorAll('.pre-edit-client-secrets'))) {
        node.classList.toggle('d-none', editing);
    }
    for (const node of Array.from(document.querySelectorAll('.edit-client-secrets'))) {
        node.classList.toggle('d-none', !editing);
    }
    const flag = one('#ClientSecretsEdited') as HTMLInputElement | null;
    if (flag) flag.value = String(editing);
}

/** Create and update differ only in verb and URL; the re-render is the same. */
function save(url: string, method: 'POST' | 'PUT'): Promise<unknown> {
    const form = one(`#${FORM}`) as HTMLFormElement | null;
    if (!form) return Promise.resolve(null);
    const resourceId = value('ResourceId');
    saving(true);
    return ajaxJson({
        url,
        method,
        data: serialize(form),
        alertInto: TAB,
        errorTitle: 'Failed to save Identity Client',
        onSuccess: () => {
            toast('success', 'Identity client saved');
            void Client.DetailByResourceId(resourceId);
        },
        onFailure: showModelState,
        onComplete: () => saving(false),
    });
}
